/**
 * DesktopContextMenu — Right-click menu on desktop
 * Options: New Folder, Change Wallpaper, etc.
 * CSS: var(--os-*) only. Icons: lucide-react only.
 */
import { FolderPlus, Image, RefreshCw } from 'lucide-react'
import { useRef, useState } from 'react'
import { API_URL, apiFetch } from '../../../config/api'

interface Props {
  x: number
  y: number
  onClose: () => void
  onFolderCreated?: () => void
}

export function DesktopContextMenu({ x, y, onClose, onFolderCreated }: Props) {
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [folderName, setFolderName] = useState('New Folder')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleNewFolder = () => {
    setCreatingFolder(true)
    setTimeout(() => inputRef.current?.select(), 50)
  }

  const confirmFolder = async () => {
    if (!folderName.trim()) return
    try {
      const res = await apiFetch(`${API_URL}/api/files/desktop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: folderName.trim() })
      })
      const data = await res.json() as { ok: boolean; error?: string }
      if (data.ok) {
        window.dispatchEvent(new CustomEvent('runai:desktop-refresh', {}))
        onFolderCreated?.()
      }
    } catch { /* silent */ }
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
        onClick={onClose}
      />
      {/* Menu */}
      <div style={{
        position: 'fixed',
        left: Math.min(x, window.innerWidth - 200),
        top: Math.min(y, window.innerHeight - 200),
        zIndex: 9999,
        background: 'var(--os-surface)',
        backdropFilter: 'blur(20px)',
        border: '1px solid var(--os-border)',
        borderRadius: 10,
        padding: '4px 0',
        minWidth: 180,
        boxShadow: 'var(--os-elevation-3)',
      }}>
        {!creatingFolder ? (
          <>
            <MenuItem icon={<FolderPlus size={13} />} label="New Folder" onClick={handleNewFolder} />
            <div style={{ height: 1, background: 'var(--os-border)', margin: '4px 0' }} />
            <MenuItem icon={<Image size={13} />} label="Change Wallpaper" onClick={() => {
              window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'os-settings' }))
              onClose()
            }} />
            <MenuItem icon={<RefreshCw size={13} />} label="Refresh Desktop" onClick={() => {
              window.dispatchEvent(new CustomEvent('runai:desktop-refresh', {}))
              onClose()
            }} />
          </>
        ) : (
          <div style={{ padding: '8px 12px' }}>
            <div style={{ fontSize: 11, color: 'var(--os-muted)', marginBottom: 6 }}>Folder name:</div>
            <input
              ref={inputRef}
              value={folderName}
              onChange={e => setFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void confirmFolder(); if (e.key === 'Escape') onClose() }}
              autoFocus
              style={{
                width: '100%', padding: '5px 8px', borderRadius: 6,
                background: 'var(--os-input-bg)', border: '1px solid var(--os-accent)',
                color: 'var(--os-text)', fontSize: 12, outline: 'none',
                fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button onClick={() => void confirmFolder()} style={{
                flex: 1, padding: '4px 0', borderRadius: 6, border: 'none',
                background: 'var(--os-accent)', color: '#fff', cursor: 'pointer',
                fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
              }}>Create</button>
              <button onClick={onClose} style={{
                flex: 1, padding: '4px 0', borderRadius: 6, border: '1px solid var(--os-border)',
                background: 'var(--os-surface)', color: 'var(--os-muted)', cursor: 'pointer',
                fontSize: 11, fontFamily: 'inherit',
              }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

function MenuItem({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px', background: hover ? 'var(--os-hover)' : 'transparent',
        border: 'none', cursor: 'pointer', color: 'var(--os-text)',
        fontSize: 12, fontFamily: 'inherit', textAlign: 'left',
      }}
    >
      <span style={{ color: 'var(--os-muted)' }}>{icon}</span>
      {label}
    </button>
  )
}
